/**
 * mRNA-1273_1283-P401 COVID-19 Vaccine Study - Self-Contained Module
 *
 * This module contains all configuration for the mRNA-1273_1283-P401 COVID-19 Vaccine Study
 * in a single, easily maintainable location.
 */

import { defineStudy } from "./registry";
import { covid50to64HealthyScreeningQuestions } from "../../../data/pre-screener-config/COVID50to64HealthyScreener";
import { modcoVP401EligibleZipCodes } from "../../../data/vaccine-study-zipcodes/modcoVP401ZipCodes";

/**
 * Common question IDs that will be asked before zipcode
 * and should be filtered out of study-specific questions
 */
const COMMON_QUESTION_IDS = ["age_eligibility", "past_vaccine_study_participation"];

/**
 * mRNA-1273_1283-P401 COVID-19 Vaccine Study
 *
 * Priority: 2 (Second priority - after Seqirus)
 * Trial ID: VMODCOVP401
 * Location: Alaska only (108 zipcodes)
 *
 * Key criteria:
 * - Age 50-64
 * - No underlying high-risk conditions
 * - Medically stable (no medication changes in 60 days)
 * - Willing to receive investigational COVID-19 vaccine
 * - No COVID infection in past 3 months
 * - No COVID vaccine in past 3 months
 * - No recent non-COVID vaccines (28 days)
 * - Not immunocompromised
 * - No immunosuppressive medications
 * - No severe allergies to mRNA vaccines
 * - No recent heart inflammation
 * - No history of Guillain-Barré syndrome
 */
export const modcoVP401Study = defineStudy({
  id: "covid-vaccine-50-64",
  name: "COVID-19 Investigational Vaccine Studies for Healthy Adults",
  trialId: "VVACCOV1125",
  priority: 2,

  // Alaska only - convert readonly array to regular array for compatibility
  eligibleZipCodes: Array.from(modcoVP401EligibleZipCodes),

  // All questions including common ones (will be filtered automatically)
  questions: covid50to64HealthyScreeningQuestions,

  // Questions to filter out (already asked in common stage)
  commonQuestionIds: COMMON_QUESTION_IDS,

  // Required common answer values (age 50-64)
  commonAnswerRequirements: {
    age_eligibility: ["50_to_64"],
  },

  metadata: {
    sponsor: "Moderna",
    description:
      "Multiple COVID-19 investigational vaccine studies are now enrolling participants, with appointments available. If eligible, participants may be matched to one of the current COVID-19 vaccine studies. Should you express interest, the study team will contact you directly to provide further details and answer any questions about study procedures, time commitment, potential risks/benefits, and compensation.",
  },
});
